import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, RefreshCw, Star, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ZorkiPublicApi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/zorki-public-api/key`);
      setData(r.data);
    } catch (e) {
      toast.error("Blad ladowania: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success("Skopiowano");
  };

  const rotate = async () => {
    if (!confirm("Wygenerowac nowy klucz? Stary zostanie natychmiast dezaktywowany.")) return;
    setRotating(true);
    try {
      await axios.post(`${API}/admin/zorki-public-api/key/rotate`);
      toast.success("Nowy klucz wygenerowany");
      await load();
    } catch (e) {
      toast.error("Blad rotacji: " + (e.response?.data?.detail || e.message));
    } finally {
      setRotating(false);
    }
  };

  if (loading) return <div className="p-6">Ladowanie...</div>;
  if (!data) return <div className="p-6">Brak danych</div>;

  const curl = `curl -H 'X-API-Key: ${data.key}' ${data.endpoint}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-2">
            <Star className="w-8 h-8 text-amber-500" />
            Zorki Public API
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Publiczne API do pobierania listy portali oznaczonych flaga "zorki_enabled"
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Klucz API + statystyki</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs uppercase text-muted-foreground">Klucz</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 p-3 bg-muted rounded font-mono text-sm break-all">{data.key}</code>
              <Button size="sm" variant="outline" onClick={() => copy(data.key)}>
                <Copy className="w-4 h-4 mr-1" /> Kopiuj
              </Button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase text-muted-foreground">Endpoint</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 p-3 bg-muted rounded font-mono text-sm break-all">{data.endpoint}</code>
              <Button size="sm" variant="outline" asChild>
                <a href={data.endpoint} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded text-center">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                {data.portals_count || 0}
              </div>
              <div className="text-xs text-muted-foreground">Portale z zorki_enabled</div>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded text-center">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                {data.usage_count || 0}
              </div>
              <div className="text-xs text-muted-foreground">Uzyc lacznie</div>
            </div>
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded text-center">
              <div className="text-xs text-muted-foreground pt-1">Ostatnie uzycie</div>
              <div className="text-xs font-medium mt-1">
                {data.last_used_at ? new Date(data.last_used_at).toLocaleString("pl-PL") : "nigdy"}
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={load} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-1" /> Odswiez
            </Button>
            <Button onClick={rotate} disabled={rotating} variant="destructive" size="sm">
              {rotating ? "Rotacja..." : "Wygeneruj nowy klucz"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test curl</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-3 bg-slate-900 text-green-400 rounded font-mono text-sm break-all">
              {curl}
            </code>
            <Button size="sm" variant="outline" onClick={() => copy(curl)}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dokumentacja dla partnerow</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p><strong>Opis:</strong> {data.docs?.description}</p>
          <p><strong>Autoryzacja:</strong> <code>{data.docs?.auth}</code></p>

          <div>
            <strong>Format odpowiedzi:</strong>
            <pre className="mt-1 p-3 bg-muted rounded overflow-x-auto text-xs">
              {JSON.stringify(data.docs?.example_response, null, 2)}
            </pre>
          </div>

          <div>
            <strong>Rate limit:</strong> {data.docs?.rate_limit}
          </div>

          {data.docs?.notes && (
            <div>
              <strong>Uwagi:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
                {data.docs.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/10">
        <CardContent className="pt-6">
          <p className="text-sm">
            <strong>Jak dodac portal do listy?</strong> Wejdz w{" "}
            <a href="/sites" className="underline text-amber-700 dark:text-amber-400">Portale</a>,
            otworz szczegoly serwisu i zaznacz w zakladce "Przeglad" checkbox
            <span className="mx-1 px-2 py-0.5 bg-amber-200 dark:bg-amber-800 rounded text-xs font-medium">
              ⭐ Spolecznosc otrzymuje 'zorki'
            </span>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
